/**
 * GMA-1228: Single source of truth for client-timezone resolution across the
 * activity write-path, read-path, edit-path, tooltip, CSV, and `ViewActivityPage`.
 *
 * Background
 * ----------
 * The web calendar grid encodes event times using the **client's** IANA zone via
 * `activityInstantToEventCalendarDate` (see `eventCalendarDates.js`). Prior to
 * this helper, `createActivity` / `updateActivity` / `useActivityForm.initData`
 * / the tooltip / the CSV / `ViewActivityPage` all used plain `moment(...)`,
 * which parses / formats in the **caregiver's browser** timezone. When caregiver
 * TZ and client TZ diverged (e.g. LA caregiver scheduling a Chicago client at
 * 7:03 AM), the caregiver saved a wall-time that displayed as 5:03 on the grid,
 * and QA reported mismatched tooltips / CSV / edit forms.
 *
 * Contract
 * --------
 *   - `resolveActivityTimezone(rawZone, options)` returns the zone we should use
 *     and emits a Sentry breadcrumb when the requested zone is missing/invalid
 *     and we fall back to `moment.tz.guess()`. The fallback is deterministic
 *     (no throw) so the app stays usable while data-quality alerts fire in
 *     Sentry.
 *
 *   - `buildZonedInstant(date, time, zone)` constructs a `moment` for a wall
 *     clock time (`YYYY-MM-DD`, `HH:mm`) anchored in the given IANA zone. This
 *     is the write-path entry point — the resulting instant is what we POST
 *     to the backend as `toISOString()`.
 *
 *   - `formatZonedWallTime(iso, zone, format)` formats an API ISO string in the
 *     client's wall-clock time. This is the read-path entry point consumed by
 *     tooltip / CSV / ViewActivity.
 *
 *   - `isValidIanaZone(zone)` is a small helper that the form banner and the
 *     write path both use to tell "user has a real zone" from "missing /
 *     gibberish" before emitting the data-quality signal.
 *
 * All breadcrumbs and capture messages live under the `activities.timezone`
 * Sentry category so the QA playbook can search a single prefix. Search strings
 * are documented in `docs/TIMEZONE_INVARIANTS.md`.
 */

import moment from "moment-timezone";
import pickerMoment from "moment";
import { addBreadcrumb, captureMessage } from "../config/sentry";

const SENTRY_CATEGORY = "activities.timezone";

/** Dedupes `[GMA-1228] activity_timezone_fallback` capture per session — see resolveActivityTimezone. */
const timezoneFallbackCaptureOnceKeys = new Set();

/** Clears fallback capture dedupe (Jest only; call from affected tests `beforeEach`). */
export function __resetTimezoneFallbackCaptureDedupeForTests() {
  timezoneFallbackCaptureOnceKeys.clear();
}

/** Same copy as Yup + UI; centralized so ActivityContext throws match form validation (GMA-176). */
export const MESSAGE_ACTIVITY_NO_PAST_START =
  "This activity can't be scheduled in the past on the client's calendar. Move the start to today or a later date.";

/** Same-day past wall-clock instant in the client zone. */
export const MESSAGE_ACTIVITY_NO_PAST_START_TIME =
  "This start time is already in the past for the client's calendar. Pick a later time or move the start to tomorrow.";

/** Returns true when `zone` is a non-empty string recognised by `moment-timezone`. */
export const isValidIanaZone = (zone) =>
  typeof zone === "string" && zone.length > 0 && !!moment.tz.zone(zone);

/** Read-only caregiver copy: canonical IANA plus abbrev + numeric offset at “now”. */
export const formatClientIanaTzReadable = (iana) => {
  if (!isValidIanaZone(iana)) return typeof iana === "string" ? iana.trim() || "" : "";
  const m = moment().tz(iana);
  return `${iana} (${m.format("z")}, ${m.format("Z")})`;
};

/**
 * #494 — the fallback for a CLIENT-SCOPED DISPLAY, which is not the activity
 * fallback below.
 *
 * `resolveActivityTimezone` falls back to `moment.tz.guess()` — the VIEWER's
 * zone. That is correct for an activity form, where the staff member is the
 * actor and their own clock is the sensible default. It is exactly wrong for
 * rendering a client's event history: it silently reproduces the defect #494
 * reports, and once a zone LABEL is attached it is worse than the original bug,
 * because it states a client-zone reading that is really the reader's own.
 *
 * The backend settled this for the caregiver-email surface (GMA-1284,
 * `resolveClientIanaTimezone` -> America/Chicago). The same caregiver reads
 * both surfaces, so they have to agree — and never UTC, which is a machine
 * clock no caregiver is on.
 */
export const CLIENT_TZ_FALLBACK = "America/Chicago";

/** The client's zone when usable, else the caregiver-facing default. Never the viewer's. */
export const resolveClientDisplayZone = (rawZone) =>
  isValidIanaZone(rawZone) ? rawZone : CLIENT_TZ_FALLBACK;

/**
 * A client-scoped instant, rendered on the CLIENT's clock and always carrying
 * its zone abbreviation. The label is not decoration: without it an hour-off
 * reading is indistinguishable from a correct one, which is how #494 shipped
 * unnoticed. The date is included because a bare time puts an event on the
 * wrong day across a midnight boundary.
 *
 * Returns an em dash rather than an empty string so a missing timestamp reads
 * as absent instead of as a rendering failure.
 */
export const formatClientZonedTime = (iso, rawZone) => {
  if (iso === null || iso === undefined || iso === "") return "\u2014";
  const m = moment(iso).tz(resolveClientDisplayZone(rawZone));
  if (!m.isValid()) return "\u2014";
  return m.format("M/D/YYYY, h:mm A z");
};

/**
 * #494 — the END of a session, rendered beside its start.
 *
 * Usually a bare clock time ("4:16 PM CDT – 4:46 PM"): no date and no repeated
 * zone, because the start immediately to its left carries both.
 *
 * But NOT always, and the exception is the whole reason this takes the start.
 * `buildStaffTimeline` pairs `bed_in`/`bed_out` with no same-day restriction,
 * so an overnight stay is a normal session — and "11:50 PM CDT – 7:00 AM"
 * silently places the end on the start's date, eight hours early. A session
 * crossing a DST boundary has the same problem in the zone: 1:30 AM CDT to
 * 1:30 AM CST reads as zero minutes under the start's label.
 *
 * So whenever the end lands on a different client-local day OR under a
 * different offset, it renders in full. The repetition is the point: it is
 * what tells the reader something changed.
 *
 * It has to exist rather than reusing the full formatter unconditionally,
 * because rendering the end on the VIEWER's clock beside a client-zoned start
 * makes a 30-minute session read as lasting -30.
 */
export const formatClientZonedSessionEnd = (endIso, startIso, rawZone) => {
  if (endIso === null || endIso === undefined || endIso === "") return "—";
  const zone = resolveClientDisplayZone(rawZone);
  const end = moment(endIso).tz(zone);
  if (!end.isValid()) return "—";

  // Reject an absent start BEFORE parsing: moment(undefined) is a valid moment
  // for the current instant, so an omitted start slips past isValid() and gets
  // compared against "now" — which silently yields a bare time whenever the end
  // happens to fall on today. null and "" do produce invalid moments, so an
  // isValid() guard alone looks correct until someone omits the argument.
  const startMissing =
    startIso === null || startIso === undefined || startIso === "";
  const start = startMissing ? null : moment(startIso).tz(zone);
  // No usable start to compare against: say everything rather than imply
  // a same-day, same-offset end we cannot vouch for.
  if (!start || !start.isValid()) return end.format("M/D/YYYY, h:mm A z");

  const sameDay = start.format("YYYY-MM-DD") === end.format("YYYY-MM-DD");
  const sameOffset = start.utcOffset() === end.utcOffset();
  return sameDay && sameOffset
    ? end.format("h:mm A")
    : end.format("M/D/YYYY, h:mm A z");
};

/**
 * #494 — the day a caregiver PICKS in a date filter is a day on the CLIENT's
 * calendar, because that is the calendar the rows are now labelled with.
 *
 * Anchoring the bounds at UTC midnight makes the filter disagree with the
 * table in a way the reader can see: for a Chicago client, "Aug 12" would pull
 * in Aug 11 from 7 PM and drop Aug 12 after 7 PM, while every row says Aug 12.
 * Rendering in the client zone without moving the bounds is therefore worse
 * than leaving both wrong together.
 *
 * `endOf("day")` is used rather than a literal 23:59:59.999 so the bound stays
 * correct on a DST-transition date, where a day is 23 or 25 hours long.
 */
export const clientDayStartIso = (yyyymmdd, rawZone) => {
  if (!yyyymmdd) return null;
  const m = moment.tz(yyyymmdd, "YYYY-MM-DD", resolveClientDisplayZone(rawZone));
  return m.isValid() ? m.startOf("day").toISOString() : null;
};

export const clientDayEndIso = (yyyymmdd, rawZone) => {
  if (!yyyymmdd) return null;
  const m = moment.tz(yyyymmdd, "YYYY-MM-DD", resolveClientDisplayZone(rawZone));
  return m.isValid() ? m.endOf("day").toISOString() : null;
};

/**
 * Resolve the zone we should use for a given client context, logging the
 * fallback path when the raw zone is missing or invalid. `source` is a short
 * tag the caller passes in (e.g. `"create"`, `"update"`, `"edit-load"`,
 * `"tooltip"`, `"csv"`, `"view-activity"`) so Sentry tells us which formatter
 * path hit the fallback.
 *
 * Optional `mode` (e.g. `"create"` | `"edit"`) is attached to breadcrumbs for
 * validation paths that share one `source` string (GMA-176).
 *
 * Returns `{ zone, usedFallback }`. Callers that need a strict requirement
 * should check `usedFallback` and degrade UX accordingly (e.g. render a banner).
 */
export const resolveActivityTimezone = (
  rawZone,
  { source, clientId, mode } = {},
) => {
  const modeTag = mode ? { mode } : {};

  if (isValidIanaZone(rawZone)) {
    addBreadcrumb("activity_timezone_resolve", {
      category: SENTRY_CATEGORY,
      level: "info",
      data: {
        ticket: "GMA-1228",
        source: source || "unknown",
        zone: rawZone,
        usedFallback: false,
        ...modeTag,
      },
    });
    return { zone: rawZone, usedFallback: false };
  }

  const fallbackZone = moment.tz.guess() || "UTC";
  addBreadcrumb("activity_timezone_fallback", {
    category: SENTRY_CATEGORY,
    level: "warning",
    data: {
      ticket: "GMA-1228",
      source: source || "unknown",
      requestedZone: rawZone ?? null,
      fallbackZone,
      clientId: clientId ?? null,
      ...modeTag,
    },
  });

  const dedupeKey = `${source || "unknown"}::${JSON.stringify(rawZone)}`;
  if (!timezoneFallbackCaptureOnceKeys.has(dedupeKey)) {
    timezoneFallbackCaptureOnceKeys.add(dedupeKey);
    // First occurrence per `{ source, requestedZone }` per page load avoids O(N)
    // Sentry Issues from the calendar (`formatActivities`). Breadcrumbs stay per call.
    captureMessage("[GMA-1228] activity_timezone_fallback", {
      level: "warning",
      tags: {
        ticket: "GMA-1228",
        action: "timezone-fallback",
        source: source || "unknown",
      },
      extra: {
        requestedZone: rawZone ?? null,
        fallbackZone,
        clientId: clientId ?? null,
      },
    });
  }

  return { zone: fallbackZone, usedFallback: true };
};

/**
 * Build a timezone-aware `moment` for a wall-clock `YYYY-MM-DD` + `HH:mm`
 * anchored in `zone`. Accepts times with or without seconds; trims whitespace.
 * Returns an invalid `moment` (caller can check `.isValid()`) when inputs are
 * empty / unparseable — we deliberately do not throw, matching the existing
 * `moment("", "HH:mm")` tolerance used elsewhere in the form.
 *
 * Passes through `resolveActivityTimezone` so every write-path construction
 * emits either the resolve breadcrumb (when the client zone is valid) or the
 * fallback breadcrumb (when we land on `moment.tz.guess()`). That keeps the
 * Sentry contract the QA playbook depends on: every save that skips a valid
 * client zone leaves a visible trail.
 */
export const buildZonedInstant = (date, time, zone, options = {}) => {
  if (typeof date !== "string" || date.trim() === "") return moment.invalid();
  if (typeof time !== "string" || time.trim() === "") return moment.invalid();
  const hhmm = moment(time.trim(), ["HH:mm", "HH:mm:ss"]).format("HH:mm");
  const { zone: resolved } = resolveActivityTimezone(zone, {
    source: options.source || "write",
    clientId: options.clientId,
  });
  return moment.tz(`${date.trim()} ${hhmm}`, "YYYY-MM-DD HH:mm", resolved);
};

/**
 * Format an API ISO instant in the client's wall-clock time. `format` is any
 * `moment` format string. Emits a formatter-path breadcrumb so we can prove
 * (via Sentry) that tooltip, CSV, ViewActivity, and the grid all resolve from
 * the same helper — a regression where one surface forgets the client zone
 * now leaves a distinctive breadcrumb trail.
 */
export const formatZonedWallTime = (
  iso,
  zone,
  format,
  { source, clientId } = {},
) => {
  if (iso === null || iso === undefined || iso === "") return "";
  const { zone: resolvedZone } = resolveActivityTimezone(zone, {
    source,
    clientId,
  });
  const m = moment(iso).tz(resolvedZone);
  if (!m.isValid()) return "";
  return m.format(format);
};

/**
 * Convenience helper for the edit-flow path: given a persisted ISO and the
 * client zone, return the `YYYY-MM-DD` and `HH:mm` the form should show. Keeps
 * `useActivityForm.initData` tiny and mirrors the write-path contract.
 */
export const extractZonedFormFields = (iso, zone, { source, clientId } = {}) => {
  if (iso === null || iso === undefined || iso === "") {
    return { date: "", time: "" };
  }
  const { zone: resolvedZone } = resolveActivityTimezone(zone, {
    source,
    clientId,
  });
  const m = moment(iso).tz(resolvedZone);
  if (!m.isValid()) return { date: "", time: "" };
  return { date: m.format("YYYY-MM-DD"), time: m.format("HH:mm") };
};

/**
 * Log a sampled `captureMessage` when the caregiver browser zone and the
 * client zone diverge at save time. This is how we size cross-TZ usage in
 * production without flooding Sentry — callers pass the detected zones and we
 * emit a single breadcrumb per save.
 */
export const reportTimezoneMismatchIfAny = ({
  caregiverZone,
  clientZone,
  action,
  clientId,
  activityId,
}) => {
  if (!caregiverZone || !clientZone) return false;
  if (caregiverZone === clientZone) return false;
  addBreadcrumb("activity_timezone_mismatch", {
    category: SENTRY_CATEGORY,
    level: "info",
    data: {
      ticket: "GMA-1228",
      action,
      caregiverZone,
      clientZone,
      clientId: clientId ?? null,
      activityId: activityId ?? null,
    },
  });
  captureMessage("[GMA-1228] activity_timezone_mismatch", {
    level: "info",
    tags: {
      ticket: "GMA-1228",
      action: action || "unknown",
    },
    extra: {
      caregiverZone,
      clientZone,
      clientId: clientId ?? null,
      activityId: activityId ?? null,
    },
  });
  return true;
};

/** Guess the caregiver's browser zone without throwing — used only for the mismatch signal. */
export const guessCaregiverZone = () => {
  try {
    return moment.tz.guess() || null;
  } catch {
    return null;
  }
};

/**
 * Normalize Activity form/API time fields for the write path. MUI TimePicker passes
 * a Moment into Formik sometimes; `moment(momentObj, "HH:mm")` treats the object as
 * an invalid parse and yields "Invalid date" — corrupting POST instants (GMA-1228 QA).
 *
 * @param {unknown} val - string `HH:mm`, Moment, Date, or empty
 * @returns {string} `HH:mm` or `""` if unrecoverable
 */
export const toWallClockHHmm = (val) => {
  if (val === null || val === undefined || val === "") return "";
  if (moment.isMoment(val)) {
    const m = val.clone();
    return m.isValid() ? m.format("HH:mm") : "";
  }
  if (val instanceof Date) {
    const m = moment(val);
    return m.isValid() ? m.format("HH:mm") : "";
  }
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (!trimmed) return "";
    const parsed = moment(trimmed, [
      "HH:mm",
      "HH:mm:ss",
      "h:mm A",
      "hh:mm A",
      moment.ISO_8601,
    ]);
    return parsed.isValid() ? parsed.format("HH:mm") : "";
  }
  return "";
};

/**
 * Yup `.transform()` for mixed TimePicker-backed fields: coerce to canonical
 * `HH:mm` so validators and `buildZonedInstant` never see trimmed raw clocks
 * like `"02:30 PM"` (PR #132 / Copilot).
 *
 * Delegates to `toWallClockHHmm`; returns `""` when unrecoverable.
 */
export const yupMixedTimeToWallHHmm = (val) => {
  if (val === null || val === undefined) return "";
  return toWallClockHHmm(val) || "";
};

/**
 * Builds a Moment for MUI TimePicker `value` from mixed Formik time state.
 * Prefer this over `moment(formik.values.start_time, "HH:mm")`, which corrupts when
 * the field already holds a Moment from the picker (GMA-1228 / GMA-176 UI parity).
 *
 * @param {unknown} val - Formik time field (`HH:mm`, Moment, Date, etc.)
 * @returns {moment.Moment | null}
 */
export const wallMomentFromFormTimeValue = (val) => {
  const hhmm = toWallClockHHmm(val);
  if (!hhmm) return null;
  // AdapterMoment (`@mui/x-date-pickers`) imports `moment` (not `moment-timezone`).
  // Build picker `value` instances with that same factory to avoid TZ-wrapper edge cases.
  const m = pickerMoment(hhmm, "HH:mm", true);
  return m.isValid() ? m : null;
};

/**
 * GMA-176: normalize wall clock coming from `@event-calendar` dateClick so Add
 * Activity initialValues aren't seeded with today's `00:00` when that's already past
 * in the client's zone.
 *
 * When the clicked `{startDate, startTimeStr}` wall instant on **client today**
 * is strictly before **now**, returns wall time at the **next safe minute** in
 * the client zone: `ceil` to minute so the value is never still in the past for
 * sub-second `now` (PR #132).
 *
 * If that minute crosses midnight relative to `startDate`, sets `nextWallDate`
 * to the resulting `YYYY-MM-DD` so the navigator can bump `selectedDate`, and
 * emits `activity.handoff.clamp_day_rollover`.
 *
 * @param {{ zone: string, startDate: string, startTimeStr: string, now?: moment.Moment }} p
 * @returns {{ wallTimeHHmm: string, nextWallDate: string|null }}
 */
export const clampPastHandoffWallTimeOnToday = ({
  zone,
  startDate,
  startTimeStr,
  now: nowOverride,
}) => {
  const unchanged = () => ({
    wallTimeHHmm: startTimeStr,
    nextWallDate: null,
  });

  if (!zone || !startDate || !startTimeStr) {
    return { wallTimeHHmm: startTimeStr || "", nextWallDate: null };
  }

  const todayStr = (nowOverride || moment.tz(zone))
    .clone()
    .startOf("day")
    .format("YYYY-MM-DD");
  if (startDate !== todayStr) {
    return unchanged();
  }

  const nowZ = (nowOverride || moment.tz(zone)).clone();
  const wallCandidate = moment.tz(
    `${startDate} ${startTimeStr}`,
    "YYYY-MM-DD HH:mm",
    zone,
  );

  if (wallCandidate.isValid() && !wallCandidate.isBefore(nowZ)) {
    return unchanged();
  }

  const tSafe = nowZ.clone().startOf("minute");
  if (tSafe.isBefore(nowZ)) {
    tSafe.add(1, "minute");
  }

  const safeDayStr = tSafe.format("YYYY-MM-DD");
  if (safeDayStr !== startDate) {
    addBreadcrumb("activity.handoff.clamp_day_rollover", {
      category: SENTRY_CATEGORY,
      level: "info",
      data: {
        ticket: "GMA-176",
        zone,
        requestedStartDate: startDate,
        safeWallDay: safeDayStr,
      },
    });
  }

  return {
    wallTimeHHmm: tSafe.format("HH:mm"),
    nextWallDate: safeDayStr !== startDate ? safeDayStr : null,
  };
};
